import { Box, Typography } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { format } from "date-fns";
import { Link } from "react-router";
import { CreatedByColumn } from "../../components/created-by-column";
import { EditablePriorityCell } from "../../components/editable-priority-cell";
import { EditableSeverityCell } from "../../components/editable-severity-cell";
import { EditableTagsCell } from "../../components/editable-tags-cell";
import { PfsScoreChip } from "../../utils/pfs-utils";
import { StatusCell } from "./status-cell";
import type { TestCase } from "./types";

export const testCaseColumns: GridColDef<TestCase>[] = [
  {
    field: "name",
    headerName: "Name",
    type: "string",
    minWidth: 150,
    flex: 1,
    sortable: true,
    renderCell: (params: GridRenderCellParams<TestCase>) => {
      // Only make clickable if it's a test type with test_data
      if (params.row.type === "test" && params.row.test_data?.id) {
        return (
          <Link
            to={`/tests-main/tests/${params.row.test_data.id}`}
            style={{
              textDecoration: "none",
              color: "inherit",
              display: "block",
              width: "100%",
              height: "100%",
            }}
          >
            <Box alignItems="center" display="flex" height="100%" width="100%">
              <Typography
                variant="body2"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontWeight: 500,
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                {params.row.name}
              </Typography>
            </Box>
          </Link>
        );
      }

      // For non-test types or missing test_data, show as regular text
      return (
        <Box alignItems="center" display="flex" height="100%" width="100%">
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontWeight: 500,
            }}
          >
            {params.row.name}
          </Typography>
        </Box>
      );
    },
  },
  {
    field: "status",
    headerName: "Status",
    minWidth: 100,
    width: 140,
    sortable: true,
    renderCell: StatusCell,
  },
  {
    field: "pfs_score",
    headerName: "PFS",
    width: 100,
    align: "center",
    headerAlign: "center",
    sortable: true,
    renderCell: (params: GridRenderCellParams<TestCase>) => {
      const pfsScore = params.row.test_data?.pfs_score;
      if (pfsScore == null) return "-";
      return <PfsScoreChip score={pfsScore} />;
    },
    valueGetter: (_value, row: TestCase) => row.test_data?.pfs_score ?? null,
    description: "Probabilistic Flakiness Score",
  },
  {
    field: "priority",
    headerName: "Priority",
    minWidth: 80,
    width: 120,
    sortable: true,
    renderCell: EditablePriorityCell,
  },
  {
    field: "severity",
    headerName: "Severity",
    minWidth: 80,
    width: 120,
    sortable: true,
    renderCell: EditableSeverityCell,
  },
  {
    field: "tags",
    headerName: "Tags",
    minWidth: 150,
    width: 280,
    sortable: false,
    renderCell: EditableTagsCell,
  },
  {
    field: "created_at",
    headerName: "Created At",
    type: "dateTime",
    minWidth: 100,
    width: 120,
    sortable: true,
    valueGetter: (value: TestCase["created_at"]) => new Date(value),
    renderCell: (params: GridRenderCellParams<TestCase>) =>
      format(new Date(params.row.created_at), "MMM d, yyyy"),
  },
  {
    field: "app_config",
    headerName: "Config",
    minWidth: 120,
    width: 140,
    sortable: true,
    filterable: true,
    valueGetter: (value: TestCase["app_config"]) => {
      return value?.name || "-";
    },
    renderCell: (params: GridRenderCellParams<TestCase>) => {
      const config = params.row.app_config;
      if (!config) return "-";
      return config.name;
    },
  },
  {
    field: "created_by",
    type: "string",
    headerName: "Created By",
    minWidth: 100,
    width: 140,
    renderCell: CreatedByColumn,
    sortable: false,
  },
];
